import { createApiService, type ApiError } from './api';
import type {
  UserProfile,
  UserProfileResponse,
  PaginatedUserProfileResponse,
  UserProfileUpdateRequest,
  BioUpdateRequest,
  PreferencesUpdateRequest,
  GdprComplianceUpdateRequest,
  TermsAcceptanceUpdateRequest,
  UserConsentUpdateRequest,
  BulkUpdateRequest,
  BulkUpdateResponse,
  ProfileAnalytics,
  ActivityRecord,
  ActivitySummary,
  PhoneNumber,
  LocationData,
  Preferences,
  GdprCompliance,
  TermsAcceptance,
  UserConsent,
  Bio,
  ProfileCompletionDetails,
} from '@/types/userProfile';
import { unwrapPaginatedResponse } from '@/utils/pagination';

const SNAKE_TO_CAMEL: Record<string, string> = {
  user_id: 'userId',
  user_name: 'userName',
  first_name: 'firstName',
  last_name: 'lastName',
  date_of_birth: 'dateOfBirth',
  phone_number: 'phoneNumber',
  country_code: 'countryCode',
  email_verified: 'emailVerified',
  profile_completion_percentage: 'profileCompletionPercentage',
  profile_picture_id: 'profilePictureId',
  is_onboarding_completed: 'isOnboardingCompleted',
  last_active_at: 'lastActiveAt',
  session_id: 'sessionId',
  external_ref_id: 'externalRefId',
  enrichment_data: 'enrichmentData',
  created_at: 'createdAt',
  updated_at: 'updatedAt',
  created_by: 'createdBy',
  last_modified_by: 'lastModifiedBy',
  preferred_language: 'preferredLanguage',
  preferred_theme: 'preferredTheme',
  additional_preferences: 'additionalPreferences',
  data_processing_consent: 'dataProcessingConsent',
  consent_given_at: 'consentGivenAt',
  consent_withdrawn_at: 'consentWithdrawnAt',
  data_export_requested: 'dataExportRequested',
  data_export_requested_at: 'dataExportRequestedAt',
  data_deletion_requested: 'dataDeletionRequested',
  data_deletion_requested_at: 'dataDeletionRequestedAt',
  data_deleted_at: 'dataDeletedAt',
  legal_basis: 'legalBasis',
  data_retention_period_days: 'dataRetentionPeriodDays',
  terms_version: 'termsVersion',
  accepted_at: 'acceptedAt',
  accepted_ip_address: 'acceptedIpAddress',
  accepted_user_agent: 'acceptedUserAgent',
  terms_accepted: 'termsAccepted',
  privacy_policy_accepted: 'privacyPolicyAccepted',
  marketing_emails: 'marketingEmails',
  sms_notifications: 'smsNotifications',
  push_notifications: 'pushNotifications',
  data_analytics: 'dataAnalytics',
  third_party_sharing: 'thirdPartySharing',
  cookie_consent: 'cookieConsent',
  consent_timestamp: 'consentTimestamp',
  consent_version: 'consentVersion',
  consent_ip_address: 'consentIpAddress',
  postal_code: 'postalCode',
  is_precise: 'isPrecise',
  user_bio: 'userBio',
  bio_info: 'bioInfo',
  bio_data: 'bioData',
  profile_summary: 'profileSummary',
  professional_summary: 'professionalSummary',
  detailed_bio: 'detailedBio',
  current_role: 'currentRole',
  current_occupation: 'currentOccupation',
  current_company: 'currentCompany',
  years_of_experience: 'yearsOfExperience',
  education_level: 'educationLevel',
  career_stage: 'careerStage',
  wins_looking_for: 'winsLookingFor',
  professional_goals: 'professionalGoals',
  wins_looking_for_ids: 'winsLookingForIds',
  professional_goal_ids: 'professionalGoalIds',
  pain_point_ids: 'painPointIds',
  skill_ids: 'skillIds',
  occupation_ids: 'occupationIds',
};

function mapKeys<T extends Record<string, unknown>>(obj: T, keyMap: Record<string, string>): Record<string, unknown> {
  if (!obj || typeof obj !== 'object') return obj as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    const key = keyMap[k] ?? k;
    if (Array.isArray(v)) {
      out[key] = v.map((item) => {
        if (item != null && typeof item === 'object' && !(item instanceof Date)) {
          return mapKeys(item as Record<string, unknown>, keyMap);
        }
        return item;
      });
    } else if (v != null && typeof v === 'object' && !(v instanceof Date)) {
      out[key] = mapKeys(v as Record<string, unknown>, keyMap);
    } else {
      out[key] = v;
    }
  }
  return out;
}

/** Extract profile from API response and normalize snake_case -> camelCase so UI fields match. */
function normalizeProfileResponse(raw: unknown): UserProfile {
  if (!raw || typeof raw !== 'object') {
    console.error('[profileApi] Cannot normalize non-object response:', raw);
    throw new Error('Invalid API response: expected an object');
  }

  const res = raw as Record<string, unknown>;
  const data = res?.data as Record<string, unknown> | undefined;
  const userProfile = res?.userProfile as Record<string, unknown> | undefined;
  const profileKey = res?.profile as Record<string, unknown> | undefined;
  const userKey = res?.user as Record<string, unknown> | undefined;
  const content = res?.content as unknown[] | undefined;
  
  // Check if the root object itself looks like a profile
  const looksLikeProfile = (obj: unknown): obj is Record<string, unknown> => {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
    const o = obj as Record<string, unknown>;
    return !!(o.userId ?? o.user_id ?? o.email ?? o.userName ?? o.user_name ?? o.id ?? o._id);
  };

  const direct = looksLikeProfile(res) ? res : null;
  
  let profile: Record<string, unknown> | null =
    (data && !Array.isArray(data) && looksLikeProfile(data)) ? data :
    (userProfile && !Array.isArray(userProfile) && looksLikeProfile(userProfile)) ? userProfile :
    (profileKey && !Array.isArray(profileKey) && looksLikeProfile(profileKey)) ? profileKey :
    (userKey && !Array.isArray(userKey) && looksLikeProfile(userKey)) ? userKey :
    (direct && !Array.isArray(direct)) ? direct : null;

  if (!profile) {
    const arrayCandidate = [data, userProfile, profileKey, userKey, content, res].find(
      (v) => Array.isArray(v) && v.length > 0 && looksLikeProfile(v[0])
    );
    if (arrayCandidate && Array.isArray(arrayCandidate)) {
      profile = arrayCandidate[0] as Record<string, unknown>;
    }
  }

  // Fallback: search for a profile-like object in all keys
  if (!profile) {
    for (const val of Object.values(res)) {
      if (looksLikeProfile(val)) {
        profile = val as Record<string, unknown>;
        break;
      }
    }
  }

  if (!profile) {
    console.warn('[profileApi] Could not find profile in response. Keys:', Object.keys(res));
    if (import.meta.env.DEV) {
      console.log('[profileApi] Raw response:', res);
    }
    // Return a dummy object with at least status so it doesn't crash, but throw if it's really empty
    throw new Error('Profile data not found in API response');
  }

  const mapped = mapKeys(profile, SNAKE_TO_CAMEL) as Record<string, unknown>;
  const phone = mapped.phoneNumber as Record<string, unknown> | undefined;
  if (phone && typeof phone === 'object') {
    mapped.phoneNumber = mapKeys(phone, SNAKE_TO_CAMEL) as unknown as PhoneNumber;
  }
  const loc = mapped.location as Record<string, unknown> | undefined;
  if (loc && typeof loc === 'object') {
    mapped.location = mapKeys(loc, SNAKE_TO_CAMEL) as unknown as LocationData;
  }
  const prefs = mapped.preferences as Record<string, unknown> | undefined;
  if (prefs && typeof prefs === 'object') {
    mapped.preferences = mapKeys(prefs, SNAKE_TO_CAMEL) as unknown as Preferences;
  }
  const gdpr = mapped.gdprCompliance as Record<string, unknown> | undefined;
  if (gdpr && typeof gdpr === 'object') {
    mapped.gdprCompliance = mapKeys(gdpr, SNAKE_TO_CAMEL) as unknown as GdprCompliance;
  }
  const terms = mapped.termsAcceptance as Record<string, unknown> | undefined;
  if (terms && typeof terms === 'object') {
    mapped.termsAcceptance = mapKeys(terms, SNAKE_TO_CAMEL) as unknown as TermsAcceptance;
  }
  const consent = mapped.userConsent as Record<string, unknown> | undefined;
  if (consent && typeof consent === 'object') {
    mapped.userConsent = mapKeys(consent, SNAKE_TO_CAMEL) as unknown as UserConsent;
  }
  // Check for bio in various possible keys (bio, userBio, bioData, etc.)
  let bio = mapped.bio as Record<string, unknown> | undefined;
  if (!bio) {
    bio = (mapped.userBio || mapped.bioData || mapped.bioInfo) as Record<string, unknown> | undefined;
  }

  // If still no bio, or if we have top-level fields that should be in bio, merge them
  const topLevelBioFields = [
    'summary', 'detailedBio', 'profileSummary', 'professionalSummary', 'currentRole', 'currentOccupation',
    'currentCompany', 'yearsOfExperience', 'educationLevel', 'careerStage', 'industry',
    'skills', 'interests', 'painPoints', 'occupations', 'winsLookingFor',
    'professionalGoals', 'facts', 'skillIds', 'occupationIds', 'painPointIds',
    'winsLookingForIds', 'professionalGoalIds'
  ];

  const bioFromTopLevel: Record<string, unknown> = {};
  let hasTopLevelBio = false;
  for (const field of topLevelBioFields) {
    if (mapped[field] !== undefined) {
      bioFromTopLevel[field] = mapped[field];
      hasTopLevelBio = true;
    }
  }

  // Also check raw object for these fields in snake_case
  const snakeTopLevelBioFields = [
    'summary', 'detailed_bio', 'profile_summary', 'current_role', 'current_occupation',
    'current_company', 'years_of_experience', 'education_level', 'career_stage', 'industry',
    'skills', 'interests', 'pain_points', 'occupations', 'wins_looking_for',
    'professional_goals', 'facts', 'skill_ids', 'occupation_ids', 'pain_point_ids',
    'wins_looking_for_ids', 'professional_goal_ids'
  ];

  for (const snakeField of snakeTopLevelBioFields) {
    if (profile[snakeField] !== undefined) {
      const camelField = SNAKE_TO_CAMEL[snakeField] || snakeField;
      if (bioFromTopLevel[camelField] === undefined) {
        bioFromTopLevel[camelField] = profile[snakeField];
        hasTopLevelBio = true;
      }
    }
  }

  if (hasTopLevelBio) {
    bio = { ...(bio || {}), ...bioFromTopLevel };
  }

  if (bio && typeof bio === 'object' && !Array.isArray(bio)) {
    const normalizedBio = mapKeys(bio, SNAKE_TO_CAMEL) as Record<string, unknown>;
    // Ensure arrays within bio are also normalized (skills, occupations, painPoints)
    if (Array.isArray(normalizedBio.skills)) {
      normalizedBio.skills = normalizedBio.skills.map((skill: unknown) => {
        if (skill && typeof skill === 'object' && !Array.isArray(skill)) {
          return mapKeys(skill as Record<string, unknown>, SNAKE_TO_CAMEL);
        }
        return skill;
      });
    }
    if (Array.isArray(normalizedBio.occupations)) {
      normalizedBio.occupations = normalizedBio.occupations.map((occ: unknown) => {
        if (occ && typeof occ === 'object' && !Array.isArray(occ)) {
          return mapKeys(occ as Record<string, unknown>, SNAKE_TO_CAMEL);
        }
        return occ;
      });
    }
    if (Array.isArray(normalizedBio.painPoints)) {
      normalizedBio.painPoints = normalizedBio.painPoints.map((pp: unknown) => {
        if (pp && typeof pp === 'object' && !Array.isArray(pp)) {
          return mapKeys(pp as Record<string, unknown>, SNAKE_TO_CAMEL);
        }
        return pp;
      });
    }
    // Normalize winsLookingFor so coach can read winId/winDescription (API may send win_id/win_description)
    if (Array.isArray(normalizedBio.winsLookingFor)) {
      normalizedBio.winsLookingFor = normalizedBio.winsLookingFor.map((w: unknown) => {
        if (w && typeof w === 'object' && !Array.isArray(w)) {
          return mapKeys(w as Record<string, unknown>, SNAKE_TO_CAMEL);
        }
        return w;
      });
    }
    // winsLookingForIds can be string[] (IDs) or object[] (e.g. { winId, winDescription }); normalize objects only
    if (Array.isArray(normalizedBio.winsLookingForIds)) {
      normalizedBio.winsLookingForIds = normalizedBio.winsLookingForIds.map((w: unknown) => {
        if (w && typeof w === 'object' && !Array.isArray(w)) {
          return mapKeys(w as Record<string, unknown>, SNAKE_TO_CAMEL);
        }
        return w;
      });
    }
    // Backend may return only painPointIds (array of IDs); build painPoints so coach/context can show them
    const painPointIdsRaw = normalizedBio.painPointIds;
    const hasPainPoints = Array.isArray(normalizedBio.painPoints) && normalizedBio.painPoints.length > 0;
    if (!hasPainPoints && Array.isArray(painPointIdsRaw)) {
      const ids: unknown[] = Array.isArray(painPointIdsRaw) ? painPointIdsRaw : [];
      normalizedBio.painPoints = ids
        .filter((id) => typeof id === 'string' && String(id).trim().length > 0)
        .map((id) => ({ painPointId: String(id), description: String(id) }));
    }
    mapped.bio = normalizedBio as unknown as Bio;
  }

  // Ensure userId and id are set
  if (!mapped.userId) {
    mapped.userId = (mapped.id || mapped._id || profile.userId || profile.user_id || profile.id || profile._id) as string;
  }
  if (!mapped.id) {
    mapped.id = (mapped.userId || mapped._id || profile.id || profile._id || profile.userId || profile.user_id) as string;
  }

  if (import.meta.env.DEV && typeof console !== 'undefined') {
    const bioObj = mapped.bio as Record<string, unknown> | undefined;
    const winsFor = bioObj?.winsLookingFor;
    const winsIds = bioObj?.winsLookingForIds;
    console.log('[profileApi] Normalized profile details:', {
      userId: mapped.userId,
      email: mapped.email,
      userName: mapped.userName,
      hasBio: !!mapped.bio,
      bioKeys: bioObj ? Object.keys(bioObj) : [],
      summary: bioObj?.summary ? String(bioObj.summary).substring(0, 30) + '...' : 'N/A',
      profileSummary: bioObj?.profileSummary ? String(bioObj.profileSummary).substring(0, 30) + '...' : 'N/A',
      winsLookingForCount: Array.isArray(winsFor) ? winsFor.length : 0,
      winsLookingForSample: Array.isArray(winsFor) ? winsFor.slice(0, 3) : 'not array',
      winsLookingForIdsCount: Array.isArray(winsIds) ? winsIds.length : 0,
    });
  }
  return mapped as unknown as UserProfile;
}

// User Profile Service API Base URL
// Uses nginx reverse proxy path for all environments (K8s, Docker, local via Vite)
const USER_PROFILE_API_BASE_URL =
  import.meta.env.VITE_FIVE_USER_PROFILE_SERVICE_URL ||
  import.meta.env.FIVE_USER_PROFILE_SERVICE_URL ||
  '/api/user-profile';

const profileApiService = createApiService(USER_PROFILE_API_BASE_URL);

// User Profile Management APIs (paths are relative to USER_PROFILE_API_BASE_URL)
export const profileApi = {
  // Get User Profile by ID (or email when used as path segment, e.g. /profiles/jude@jpmc.com)
  getById: async (userId: string): Promise<UserProfile> => {
    const response = await profileApiService.get<unknown>(
      `/api/v1/profiles/${encodeURIComponent(userId)}`
    );
    return normalizeProfileResponse(response);
  },

  // Get User Profile by Email
  getByEmail: async (email: string): Promise<UserProfile> => {
    const response = await profileApiService.get<unknown>(
      `/api/v1/profiles/email/${encodeURIComponent(email)}`
    );
    return normalizeProfileResponse(response);
  },

  // Get User Profile by Username
  getByUsername: async (userName: string): Promise<UserProfile> => {
    const response = await profileApiService.get<unknown>(
      `/api/v1/profiles/username/${encodeURIComponent(userName)}`
    );
    return normalizeProfileResponse(response);
  },

  // Get User Profile by Username or ID (backward compatibility)
  // When identifier looks like an email, try email first so /admin/profiles/user@example.com works.
  getByUsernameOrId: async (identifier: string): Promise<UserProfile> => {
    const isEmail = identifier.includes('@');
    if (isEmail) {
      try {
        return await profileApi.getByEmail(identifier);
      } catch (emailErr: unknown) {
        const e = emailErr as { status?: number };
        if (e?.status === 404) {
          try {
            return await profileApi.getById(identifier);
          } catch {
            // fall through to rethrow email error
          }
        }
        throw emailErr;
      }
    }
    try {
      return await profileApi.getByUsername(identifier);
    } catch (error: unknown) {
      const err = error as { status?: number };
      if (err?.status === 404) {
        try {
          return await profileApi.getById(identifier);
        } catch {
          // ignore
        }
      }
      throw error;
    }
  },

  // Get All Profiles (Paginated)
  list: async (params?: {
    page?: number;
    size?: number;
    sort?: string;
  }): Promise<PaginatedUserProfileResponse> => {
    const queryParams = new URLSearchParams();
    // Spring Boot pagination uses 0-indexed pages
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());
    if (params?.sort) {
      // Handle sort parameter - could be "field,direction" format
      queryParams.append('sort', params.sort);
    }

    const query = queryParams.toString();
    const endpoint = `/api/v1/profiles${query ? `?${query}` : ''}`;
    
    try {
      const response = await profileApiService.get<unknown>(endpoint);
      const transformed = unwrapPaginatedResponse<UserProfile>(response);
      
      // Convert to PaginatedUserProfileResponse format (with flat structure for compatibility)
      return {
        ...transformed,
        totalElements: transformed.page.totalElements,
        totalPages: transformed.page.totalPages,
        size: transformed.page.size,
        number: transformed.page.number,
      } as PaginatedUserProfileResponse;
    } catch (error: unknown) {
      const apiError = error as { message?: string; status?: number; details?: unknown };
      console.error('[profileApi.list] Error fetching profiles from API:', {
        endpoint,
        error: apiError.message,
        status: apiError.status,
        details: apiError.details,
      });
      throw error;
    }
  },

  // Update Full User Profile
  update: async (userId: string, data: UserProfileUpdateRequest): Promise<UserProfile> => {
    const response = await profileApiService.patch<UserProfileResponse>(
      `/api/v1/profiles/${userId}`,
      data
    );
    return response.data || response as unknown as UserProfile;
  },

  // Update Bio (Partial Update)
  updateBio: async (userId: string, bio: BioUpdateRequest): Promise<UserProfile> => {
    const response = await profileApiService.patch<UserProfileResponse>(
      `/api/v1/profiles/${userId}/bio`,
      bio
    );
    return response.data || response as unknown as UserProfile;
  },

  // Generate Profile Summary (AI-Powered)
  generateSummary: async (userId: string): Promise<UserProfile> => {
    const response = await profileApiService.post<UserProfileResponse>(
      `/api/v1/profiles/${userId}/summary`,
      {}
    );
    return response.data || response as unknown as UserProfile;
  },

  // Update Profile Completion
  updateCompletion: async (userId: string): Promise<UserProfile> => {
    const response = await profileApiService.post<UserProfileResponse>(
      `/api/v1/profiles/${userId}/completion`,
      {}
    );
    return response.data || response as unknown as UserProfile;
  },

  // Update Last Active
  updateLastActive: async (userId: string): Promise<void> => {
    await profileApiService.post<void>(
      `/api/v1/profiles/${userId}/activity`,
      {}
    );
  },

  // Get Profiles by Completion Status
  getByCompletion: async (
    completed: boolean,
    params?: { page?: number; size?: number }
  ): Promise<PaginatedUserProfileResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());

    const query = queryParams.toString();
    const response = await profileApiService.get<PaginatedUserProfileResponse>(
      `/api/v1/profiles/completion/${completed}${query ? `?${query}` : ''}`
    );
    return response;
  },

  // Search Profiles by Skills
  searchBySkills: async (
    skillIds: string[],
    params?: { page?: number; size?: number }
  ): Promise<PaginatedUserProfileResponse> => {
    const queryParams = new URLSearchParams();
    queryParams.append('skillIds', skillIds.join(','));
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());

    const query = queryParams.toString();
    return profileApiService.get<PaginatedUserProfileResponse>(
      `/api/v1/profiles/search/skills?${query}`
    );
  },

  // Search Profiles by Interests
  searchByInterests: async (
    interests: string[],
    params?: { page?: number; size?: number }
  ): Promise<PaginatedUserProfileResponse> => {
    const queryParams = new URLSearchParams();
    queryParams.append('interests', interests.join(','));
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());

    const query = queryParams.toString();
    return profileApiService.get<PaginatedUserProfileResponse>(
      `/api/v1/profiles/search/interests?${query}`
    );
  },

  // Get Profiles by Location
  getByLocation: async (
    params: {
      country?: string;
      state?: string;
      city?: string;
      page?: number;
      size?: number;
    }
  ): Promise<PaginatedUserProfileResponse> => {
    const queryParams = new URLSearchParams();
    if (params.country) queryParams.append('country', params.country);
    if (params.state) queryParams.append('state', params.state);
    if (params.city) queryParams.append('city', params.city);
    if (params.page !== undefined) queryParams.append('page', params.page.toString());
    if (params.size !== undefined) queryParams.append('size', params.size.toString());

    const query = queryParams.toString();
    return profileApiService.get<PaginatedUserProfileResponse>(
      `/api/v1/profiles/location?${query}`
    );
  },

  // Get Inactive Users
  getInactive: async (
    days: number,
    params?: { page?: number; size?: number }
  ): Promise<PaginatedUserProfileResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());

    const query = queryParams.toString();
    return profileApiService.get<PaginatedUserProfileResponse>(
      `/api/v1/profiles/inactive/${days}${query ? `?${query}` : ''}`
    );
  },

  // Delete User Profile
  delete: async (userId: string): Promise<void> => {
    await profileApiService.delete<void>(`/api/v1/profiles/${userId}`);
  },

  // Get Analytics
  getAnalytics: async (): Promise<ProfileAnalytics> => {
    return profileApiService.get<ProfileAnalytics>(`/api/v1/profiles/analytics`);
  },

  // Check Profile Exists
  exists: async (userId: string): Promise<boolean> => {
    try {
      await profileApiService.get(`/api/v1/profiles/${userId}/exists`);
      return true;
    } catch {
      return false;
    }
  },

  // Update Preferences
  updatePreferences: async (
    userId: string,
    preferences: PreferencesUpdateRequest
  ): Promise<UserProfile> => {
    const response = await profileApiService.patch<UserProfileResponse>(
      `/api/v1/profiles/${userId}/preferences`,
      preferences
    );
    return response.data || response as unknown as UserProfile;
  },

  // Update GDPR Compliance
  updateGdprCompliance: async (
    userId: string,
    compliance: GdprComplianceUpdateRequest
  ): Promise<UserProfile> => {
    const response = await profileApiService.patch<UserProfileResponse>(
      `/api/v1/profiles/${userId}/gdpr-compliance`,
      compliance
    );
    return response.data || response as unknown as UserProfile;
  },

  // Update Terms Acceptance
  updateTermsAcceptance: async (
    userId: string,
    terms: TermsAcceptanceUpdateRequest
  ): Promise<UserProfile> => {
    const response = await profileApiService.patch<UserProfileResponse>(
      `/api/v1/profiles/${userId}/terms-acceptance`,
      terms
    );
    return response.data || response as unknown as UserProfile;
  },

  // Update User Consent
  updateUserConsent: async (
    userId: string,
    consent: UserConsentUpdateRequest
  ): Promise<UserProfile> => {
    const response = await profileApiService.patch<UserProfileResponse>(
      `/api/v1/profiles/${userId}/user-consent`,
      consent
    );
    return response.data || response as unknown as UserProfile;
  },

  // Get Profile Completion Details
  getCompletionDetails: async (userId: string): Promise<ProfileCompletionDetails> => {
    return profileApiService.get<ProfileCompletionDetails>(
      `/api/v1/profiles/${userId}/completion-details`
    );
  },

  /**
   * Update profile picture (content management ID).
   * Backend: PATCH /api/v1/profiles/{userId}/profile-picture with body { profilePictureId }.
   * If the user-profile service does not expose this yet, implement it there.
   */
  updateProfilePicture: async (userId: string, profilePictureId: string): Promise<UserProfile> => {
    const response = await profileApiService.patch<UserProfileResponse>(
      `/api/v1/profiles/${userId}/profile-picture`,
      { profilePictureId }
    );
    return response.data || (response as unknown as UserProfile);
  },
};

// Bulk Operations
export const bulkProfileApi = {
  // Bulk Update Profiles
  bulkUpdate: async (request: BulkUpdateRequest): Promise<BulkUpdateResponse> => {
    return profileApiService.put<BulkUpdateResponse>(
      `/api/v1/bulk/profiles`,
      request
    );
  },
};

// Activity Tracking APIs
export const activityApi = {
  // Track Activity
  track: async (activity: ActivityRecord): Promise<void> => {
    await profileApiService.post<void>(`/api/v1/activities/track`, activity);
  },

  // Get User Activity Summary
  getActivitySummary: async (
    userId: string,
    period: 'DAY' | 'WEEK' | 'MONTH' | 'YEAR' = 'WEEK'
  ): Promise<ActivitySummary> => {
    return profileApiService.get<ActivitySummary>(
      `/api/v1/activities/user/${userId}/summary?period=${period}`
    );
  },
};

export { profileApiService };
export type { ApiError };







